import {type DocumentNode, gql} from '@apollo/client';
import {
    type Filter,
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
    type RequestPayload,
} from '@igp/shared/api';
import type {
    AgentRefereeRevenueReportFilter,
    QueryExportAgentPlayerReportingsV2Args,
    QueryGetAgentRevenueShareWeeklyReportV2Args,
} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';

import type {
    AgentLevelReportV2DownloadFields,
    AgentLevelReportV2QueryKeys,
    AgentLevelReportV2ServerFilterKeys,
    AgentLevelReportV2ServerTextFilterKeys,
    agentLevelReportV2ServerTextFilters,
    QueryType,
} from './types';

export abstract class AgentLevelReportV2FilterBuilder<TQueryArgs, TQueryFields extends string> extends GqlRequestBuilder<
    EntityType,
    TQueryArgs,
    TQueryFields,
    AgentLevelReportV2ServerFilterKeys
> {
    protected validateFilters(_filter: Partial<Record<AgentLevelReportV2ServerFilterKeys, unknown>>): void {
        return;
    }

    protected buildFilter(filter: Filter<AgentLevelReportV2ServerFilterKeys>): {filter: AgentRefereeRevenueReportFilter} {
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: AgentLevelReportV2ServerTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                is_downstream: this.toGQLBooleanFilter(filter, 'is_downstream'),
                row_id: this.toStringFilter(filter, 'id'),
                week_start_date_ts: this.toGQLDateRange(
                    this.getLatestTimestamp(filter['week_start_date_ts.from'] as number, filter['week_start_date_ts.min'] as number),
                    filter['week_start_date_ts.to']
                ),
                daily_aggregation_range: this.toGQLDateRange(
                    this.getLatestTimestamp(
                        filter['daily_aggregation_range.from'] as number,
                        filter['daily_aggregation_range.min'] as number
                    ),
                    filter['daily_aggregation_range.to']
                ),
            },
        };
    }

    private readonly filterFieldsMapper: Record<AgentLevelReportV2ServerTextFilterKeys, agentLevelReportV2ServerTextFilters[]> = {
        agent_uid: ['agent_uid'],
        agent_username: ['agent_username'],
        agent_uid_username: ['agent_uid', 'agent_username'],
    };
}

export class AgentLevelReportV2RequestBuilder
    extends AgentLevelReportV2FilterBuilder<QueryGetAgentRevenueShareWeeklyReportV2Args, AgentLevelReportV2QueryKeys>
    implements IDownloadByLinkRequestBuilder<QueryExportAgentPlayerReportingsV2Args, AgentLevelReportV2DownloadFields>
{
    public buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<AgentLevelReportV2DownloadFields>
    ): GqlRequest<QueryExportAgentPlayerReportingsV2Args> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: filter?.type === 'daily' ? this.buildExportDailyQuery() : this.buildExportWeeklyQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected buildQuery(_fields: AgentLevelReportV2QueryKeys[]): DocumentNode {
        throw new Error('Method should not be called.');
    }
    public buildRequest(
        requestPayload: RequestPayload<AgentLevelReportV2QueryKeys>
    ): GqlRequest<QueryGetAgentRevenueShareWeeklyReportV2Args> {
        const filter = this.mapToFilterObject(requestPayload.filter);

        return !this.isFilterContainInvalidValue(filter)
            ? {
                  query: this.getQueryByFilter(requestPayload.fields, filter.type as QueryType),
                  variables: this.getGqlPageArgs(filter),
              }
            : null;
    }

    private getQueryByFilter(fields: AgentLevelReportV2QueryKeys[], type: QueryType): DocumentNode {
        let result: DocumentNode;
        if (type === 'daily') {
            result = this.buildDailyQuery(fields);
        } else {
            result = this.buildWeeklyQuery(fields);
        }
        return result;
    }

    private buildWeeklyQuery(fields: AgentLevelReportV2QueryKeys[]): DocumentNode {
        const requiredFields: AgentLevelReportV2QueryKeys[] = [
            'items.agent_uid',
            'items.row_id',
            'total_count',
            'items.week_start_date_ts.seconds',
        ];
        return gql`
            query GetAgentRevenueShareWeeklyReportV2($filter: AgentRefereeRevenueReportFilter, $sort: Sorting, $start: Int, $end: Int) {
                getAgentRevenueShareWeeklyReportV2(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields([...fields, ...requiredFields])}
                }
            }
        `;
    }

    private buildDailyQuery(fields: AgentLevelReportV2QueryKeys[]): DocumentNode {
        const requiredFields: AgentLevelReportV2QueryKeys[] = ['items.agent_uid', 'items.row_id', 'total_count'];
        return gql`
            query GetAgentRevenueShareDailyReport($filter: AgentRefereeRevenueReportFilter, $sort: Sorting, $start: Int, $end: Int) {
                getAgentRevenueShareDailyReport(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields([...fields, ...requiredFields])}
                }
            }
        `;
    }

    private buildExportWeeklyQuery() {
        return gql`
            query ExportAgentRevenueShareWeeklyReportV2(
                $filter: AgentRefereeRevenueReportFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportAgentRevenueShareWeeklyReportV2(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }

    private buildExportDailyQuery() {
        return gql`
            query ExportAgentRevenueShareDailyReport(
                $filter: AgentRefereeRevenueReportFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportAgentRevenueShareDailyReport(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }
}
